import React, { useState, useEffect, useMemo, useRef } from 'react';

import { FloatingInput } from '@components/ui/input-floating';
import { DropdownFloating } from '@components/ui/dropdown-floating';
import { useOnboardingStore } from '@features/onboarding/store/onboarding';
import { useApplicationStore } from 'src/store/application';
import { AI_HOOKS_IDS } from '@features/onboarding/constants';
import { useAIStore } from 'src/store/ai';
import { formatSSN, validateSSN } from '@utils/ssn';
import { getStateCodeByZipcode } from '@utils/zipcode';
import { cn } from '@utils/index';

interface AddressStepProps {
  handleNextCompatible: (e?: React.BaseSyntheticEvent) => void;
  setContainerClassName?: (className: string) => void;
  handlePrev?: (e?: React.BaseSyntheticEvent) => void;
  onValidationChange?: (isValid: boolean) => void;
  attemptedSubmit?: number;
}

interface AddressData {
  address1: string;
  address2: string;
  city: string;
  state: string;
  zipcode: string;
  country: string;
}

const MemberIcon = (props: React.SVGProps<SVGSVGElement>) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 47.58 47.48" {...props}>
    <circle cx={23.74} cy={24.82} r={17.17} fill="currentColor" />
    <path
      d="M29.74 32.82c0-3.31-2.69-6-6-6s-6 2.69-6 6"
      fill="none"
      stroke="#fff"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
    />
    <circle
      cx={23.74}
      cy={22.82}
      r={4}
      fill="none"
      stroke="#fff"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
    />
    <circle
      cx={23.74}
      cy={24.82}
      r={10}
      fill="none"
      stroke="#fff"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
    />
  </svg>
);

const US_STATES = [
  { value: 'AL', label: 'Alabama' },
  { value: 'AK', label: 'Alaska' },
  { value: 'AZ', label: 'Arizona' },
  { value: 'AR', label: 'Arkansas' },
  { value: 'CA', label: 'California' },
  { value: 'CO', label: 'Colorado' },
  { value: 'CT', label: 'Connecticut' },
  { value: 'DE', label: 'Delaware' },
  { value: 'DC', label: 'District of Columbia' },
  { value: 'FL', label: 'Florida' },
  { value: 'GA', label: 'Georgia' },
  { value: 'HI', label: 'Hawaii' },
  { value: 'ID', label: 'Idaho' },
  { value: 'IL', label: 'Illinois' },
  { value: 'IN', label: 'Indiana' },
  { value: 'IA', label: 'Iowa' },
  { value: 'KS', label: 'Kansas' },
  { value: 'KY', label: 'Kentucky' },
  { value: 'LA', label: 'Louisiana' },
  { value: 'ME', label: 'Maine' },
  { value: 'MD', label: 'Maryland' },
  { value: 'MA', label: 'Massachusetts' },
  { value: 'MI', label: 'Michigan' },
  { value: 'MN', label: 'Minnesota' },
  { value: 'MS', label: 'Mississippi' },
  { value: 'MO', label: 'Missouri' },
  { value: 'MT', label: 'Montana' },
  { value: 'NE', label: 'Nebraska' },
  { value: 'NV', label: 'Nevada' },
  { value: 'NH', label: 'New Hampshire' },
  { value: 'NJ', label: 'New Jersey' },
  { value: 'NM', label: 'New Mexico' },
  { value: 'NY', label: 'New York' },
  { value: 'NC', label: 'North Carolina' },
  { value: 'ND', label: 'North Dakota' },
  { value: 'OH', label: 'Ohio' },
  { value: 'OK', label: 'Oklahoma' },
  { value: 'OR', label: 'Oregon' },
  { value: 'PA', label: 'Pennsylvania' },
  { value: 'RI', label: 'Rhode Island' },
  { value: 'SC', label: 'South Carolina' },
  { value: 'SD', label: 'South Dakota' },
  { value: 'TN', label: 'Tennessee' },
  { value: 'TX', label: 'Texas' },
  { value: 'UT', label: 'Utah' },
  { value: 'VT', label: 'Vermont' },
  { value: 'VA', label: 'Virginia' },
  { value: 'WA', label: 'Washington' },
  { value: 'WV', label: 'West Virginia' },
  { value: 'WI', label: 'Wisconsin' },
  { value: 'WY', label: 'Wyoming' },
];

export const AddressStep: React.FC<AddressStepProps> = ({
  handleNextCompatible,
  setContainerClassName,
  onValidationChange,
  attemptedSubmit,
}) => {
  const { data, setData } = useOnboardingStore();
  const { applications } = useApplicationStore();
  const firstName = useMemo(() => {
    const primary = data?.applicants?.find((a) => a.applicant === 'primary');
    return primary?.firstName || '';
  }, [data]);

  const applicants = useMemo(() => data?.applicants || [], [data]);

  const [addressData, setAddressData] = useState<AddressData>({
    address1: '',
    address2: '',
    city: '',
    state: '',
    zipcode: '',
    country: 'US',
  });
  const [ssnValues, setSsnValues] = useState<Record<number, string>>({});
  const [ssnErrors, setSsnErrors] = useState<Record<number, string>>({});
  const [errors, setErrors] = useState<
    Record<'zipcode' | 'address1' | 'address2' | 'city' | 'state', string>
  >({
    zipcode: '',
    address1: '',
    address2: '',
    city: '',
    state: '',
  });
  const lastFetchedZipRef = useRef<string>('');
  const [isFetchingZip, setIsFetchingZip] = useState(false);
  const { setCurrentPage } = useAIStore();

  // Initialize data from onboarding data or applications[0] if available
  useEffect(() => {
    setTimeout(() => {
      setCurrentPage(AI_HOOKS_IDS.ADDRESS_SSN);
    }, 200);
    // First priority: check onboarding data
    if (data?.address && typeof data.address === 'object') {
      const addr = data.address;
      if (addr.address1 || addr.city || addr.state || addr.zipcode) {
        setAddressData({
          address1: addr.address1 || '',
          address2: addr.address2 || '',
          city: addr.city || '',
          state: addr.state || '',
          zipcode: addr.zipcode || '',
          country: addr.country || 'US',
        });
      }
    } else if (applications && applications.length > 0) {
      // Second priority: check applications[0]
      const app = applications[0];

      if (app.address && typeof app.address === 'object') {
        const addr = app.address as AddressData;
        if (addr.address1 || addr.city || addr.state || addr.zipcode) {
          setAddressData({
            address1: addr.address1 || '',
            address2: addr.address2 || '',
            city: addr.city || '',
            state: addr.state || '',
            zipcode: addr.zipcode || '',
            country: addr.country || 'US',
          });
        }
      }
    }

    // Initialize SSN values from applicants
    if (data?.applicants) {
      const initialSsn: Record<number, string> = {};
      data.applicants.forEach((applicant, index) => {
        if (applicant.ssn) {
          initialSsn[index] = applicant.ssn;
        }
      });
      if (Object.keys(initialSsn).length > 0) {
        setSsnValues(initialSsn);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const validateField = (field: keyof typeof errors, value: string) => {
    switch (field) {
      case 'zipcode':
        if (!value) {
          return 'Zip Code is required';
        }
        if (!/^\d{5}$/.test(value)) {
          return 'Zip Code must be 5 digits';
        }
        return '';
      case 'address1':
        if (!value.trim()) {
          return 'Address 1 is required';
        }
        return '';
      case 'city':
        if (!value.trim()) {
          return 'City is required';
        }
        return '';
      case 'state':
        if (!value.trim()) {
          return 'State is required';
        }
        return '';
      case 'address2':
        return '';
      default:
        return '';
    }
  };

  const isFormValid = useMemo(() => {
    const address1Valid = !!addressData.address1.trim();
    const cityValid = !!addressData.city.trim();
    const stateValid = !!addressData.state.trim();
    const zipcodeValid = /^\d{5}$/.test(addressData.zipcode);
    const allSsnValid = applicants.every((_, index) =>
      validateSSN(ssnValues[index] || '')
    );
    return (
      address1Valid && cityValid && stateValid && zipcodeValid && allSsnValid
    );
  }, [
    addressData.address1,
    addressData.city,
    addressData.state,
    addressData.zipcode,
    ssnValues,
    applicants,
  ]);

  useEffect(() => {
    onValidationChange?.(isFormValid);
  }, [isFormValid, onValidationChange]);

  useEffect(() => {
    if (attemptedSubmit && attemptedSubmit > 0) {
      console.log('attemptedSubmit', attemptedSubmit);
      const nextErrors: typeof errors = {
        zipcode: validateField('zipcode', addressData.zipcode),
        address1: validateField('address1', addressData.address1),
        address2: validateField('address2', addressData.address2),
        city: validateField('city', addressData.city),
        state: validateField('state', addressData.state),
      };
      setErrors(nextErrors);

      // Validate SSN for all applicants
      const nextSsnErrors: Record<number, string> = {};
      applicants.forEach((_, index) => {
        const ssn = ssnValues[index] || '';
        if (!ssn) {
          nextSsnErrors[index] = 'SSN is required';
        } else if (!validateSSN(ssn)) {
          nextSsnErrors[index] = 'Please enter a valid SSN';
        } else {
          nextSsnErrors[index] = '';
        }
      });
      setSsnErrors(nextSsnErrors);
    } else {
      setErrors({
        zipcode: '',
        address1: '',
        address2: '',
        city: '',
        state: '',
      });
      setSsnErrors({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attemptedSubmit]);

  const setApplicationAddress = (formData: AddressData) => {
    const addressObj = {
      ...(formData?.address1?.trim() && { address1: formData.address1 }),
      ...(formData?.address2?.trim() && { address2: formData.address2 }),
      ...(formData?.city?.trim() && { city: formData.city }),
      ...(formData?.state?.trim() && { state: formData.state }),
      ...(formData?.zipcode?.trim() && { zipcode: formData.zipcode }),
      ...(formData?.country?.trim() && { country: formData.country }),
    };
    // Save to onboarding store (primary source)
    setData({
      ...data,
      address: addressObj,
    });

    // Also update application store for compatibility
    const application = useApplicationStore.getState().applications[0];
    if (application) {
      application.address = {
        address1: formData.address1 || null,
        address2: formData.address2 || null,
        city: formData.city || null,
        state: formData.state || null,
        zipcode: formData.zipcode || null,
        country: formData.country || null,
      };
      useApplicationStore.setState({ applications: [application] });
    }
  };

  // Save form data when address changes
  useEffect(() => {
    if (
      addressData.zipcode ||
      addressData.address1 ||
      addressData.address2 ||
      addressData.city ||
      addressData.state
    ) {
      const formData = {
        address1: addressData.address1,
        address2: addressData.address2,
        city: addressData.city,
        state: addressData.state,
        zipcode: addressData.zipcode,
        country: addressData.country,
      };
      setApplicationAddress(formData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addressData]);

  // Save SSN values to onboarding store when they change
  useEffect(() => {
    if (!data?.applicants) return;
    const updatedApplicants = data.applicants.map((applicant, index) => ({
      ...applicant,
      ssn: ssnValues[index] || applicant.ssn || '',
    }));
    const hasChanges = updatedApplicants.some(
      (a, i) => a.ssn !== (data.applicants?.[i]?.ssn || '')
    );
    if (hasChanges) {
      setData({
        ...data,
        applicants: updatedApplicants,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ssnValues]);

  useEffect(() => {
    if (!addressData.zipcode) {
      lastFetchedZipRef.current = '';
    }
  }, [addressData.zipcode]);

  // Look up city/state by zip and prefill the address fields.
  const fetchAddressByZip = async (zip: string) => {
    if (zip.length !== 5 || zip === lastFetchedZipRef.current) return;
    setIsFetchingZip(true);
    try {
      const response = await fetch(`https://zip.getziptastic.com/v2/US/${zip}`);
      if (!response.ok) {
        throw new Error('Failed to fetch ZIP info');
      }
      const result = await response.json();
      setAddressData((prev) => ({
        ...prev,
        city: result.city || prev.city,
        state: result.state_short || prev.state,
        country: result.country || prev.country || 'US',
      }));
      setErrors((prev) => ({ ...prev, city: '', state: '' }));
      lastFetchedZipRef.current = zip;
    } catch (error) {
      console.error('ZIP lookup error:', error);
      setErrors((prev) => ({
        ...prev,
        zipcode:
          'Unable to auto-fill city and state. Please enter them manually.',
      }));
      lastFetchedZipRef.current = '';
    } finally {
      setIsFetchingZip(false);
    }
  };

  const handleZipChange = async (value: string) => {
    const digitsOnly = value.replace(/\D/g, '').slice(0, 5);
    setAddressData((prev) => ({ ...prev, zipcode: digitsOnly }));
    setErrors((prev) => ({ ...prev, zipcode: '' }));
    if (digitsOnly.length < 5) {
      lastFetchedZipRef.current = '';
      return;
    }
    await fetchAddressByZip(digitsOnly);
  };

  // Prefill city/state by default from the zip already entered on the personal
  // info step (don't wait for a zip change on this page).
  useEffect(() => {
    const addr =
      data?.address && typeof data.address === 'object'
        ? data.address
        : applications?.[0]?.address &&
            typeof applications[0].address === 'object'
          ? (applications[0].address as AddressData)
          : undefined;
    const zip = (addr?.zipcode || '').replace(/\D/g, '').slice(0, 5);
    // Re-fetch if city/state are missing OR the stored state no longer matches
    // the zip (e.g., an existing user changed their zip on the personal info step).
    const expectedState = getStateCodeByZipcode(zip);
    const hasCorrectCityState = !!(
      addr?.city &&
      addr?.state &&
      addr.state === expectedState
    );
    if (zip.length === 5 && !hasCorrectCityState) {
      fetchAddressByZip(zip);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFieldChange = (
    field: keyof AddressData,
    value: string,
    errorKey: keyof typeof errors
  ) => {
    setAddressData((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [errorKey]: '' }));
  };

  const handleSsnChange = (index: number, value: string) => {
    const formatted = formatSSN(value);
    setSsnValues((prev) => ({ ...prev, [index]: formatted }));
    setSsnErrors((prev) => ({ ...prev, [index]: '' }));
  };

  const getApplicantIcon = (applicant: { applicant?: string }) => {
    if (applicant.applicant === 'spouse') return '/icons/ssn-screen/spouse.svg';
    return '/icons/ssn-screen/child.svg';
  };

  const getApplicantRole = (applicant: { applicant?: string }) => {
    if (applicant.applicant === 'primary') return 'Primary member';
    if (applicant.applicant === 'spouse') return 'Spouse/Domestic Partner';
    return 'Child - Dependent';
  };

  return (
    <div className="flex flex-col gap-6 w-full max-w-2xl mx-auto sm:pt-4">
      {/* Address section */}
      <h3 className="text-lg md:text-xl font-black leading-tight">
        {firstName}, pop in your address when you&apos;re ready.
      </h3>
      <div className="flex flex-col gap-4">
        {/* Row 1: Zip | Address 1 | Address 2 */}
        <div className="grid grid-cols-1 sm:grid-cols-[1fr_2fr_2fr] gap-3">
          <div>
            <FloatingInput
              id="zip"
              label="Zip code"
              type="text"
              value={addressData.zipcode}
              onChange={(e) => handleZipChange(e.target.value)}
              maxLength={5}
              inputClass={cn(
                'h-12 min-h-auto outline-none border border-[#B7B7B7] rounded-lg px-3 disabled:opacity-60 disabled:cursor-not-allowed',
                errors.zipcode && '!border-red-500 !border-2'
              )}
              disabled
            />
            {errors.zipcode && (
              <p className="text-sm text-red-500 mt-1 text-left">
                {errors.zipcode}
              </p>
            )}
          </div>
          <div>
            <FloatingInput
              id="address1"
              label="Address 1"
              type="text"
              value={addressData.address1}
              onChange={(e) =>
                handleFieldChange('address1', e.target.value, 'address1')
              }
              inputClass={cn(
                'h-12 min-h-auto outline-none border border-[#B7B7B7] rounded-lg px-3',
                errors.address1 && '!border-red-500 !border-2'
              )}
            />
            {errors.address1 && (
              <p className="text-sm text-red-500 mt-1 text-left">
                {errors.address1}
              </p>
            )}
          </div>
          <div>
            <FloatingInput
              id="address2"
              label="Address 2"
              type="text"
              value={addressData.address2}
              onChange={(e) =>
                handleFieldChange('address2', e.target.value, 'address2')
              }
              inputClass={cn(
                'h-12 min-h-auto outline-none border border-[#B7B7B7] rounded-lg px-3',
                errors.address2 && '!border-red-500 !border-2'
              )}
            />
            {errors.address2 && (
              <p className="text-sm text-red-500 mt-1 text-left">
                {errors.address2}
              </p>
            )}
          </div>
        </div>
        {/* Row 2: City | State */}
        <div className="grid grid-cols-1 sm:grid-cols-[2fr_2fr] gap-3 sm:max-w-[66%]">
          <div>
            <FloatingInput
              id="city"
              label="City"
              type="text"
              value={addressData.city}
              onChange={(e) =>
                handleFieldChange('city', e.target.value, 'city')
              }
              inputClass={cn(
                'h-12 min-h-auto outline-none border border-[#B7B7B7] rounded-lg px-3',
                errors.city && '!border-red-500 !border-2'
              )}
            />
            {errors.city && (
              <p className="text-sm text-red-500 mt-1 text-left">
                {errors.city}
              </p>
            )}
          </div>
          <div className="text-left">
            <DropdownFloating
              id="state"
              label="State"
              value={addressData.state}
              onChange={(value) =>
                handleFieldChange('state', value as string, 'state')
              }
              options={US_STATES}
              inputClass={cn(
                'border border-[#B7B7B7] rounded-lg w-full h-12',
                errors.state && '!border-red-500 !border-2'
              )}
              error={!!errors.state}
            />
            {errors.state && (
              <p className="text-sm text-red-500 mt-1 text-left">
                {errors.state}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* SSN section */}
      {applicants.length > 0 && (
        <>
          <div className="border-t border-gray-200 pt-6 mt-2">
            <h3 className="text-lg md:text-xl font-black leading-tight">
              Lastly, we need the Social Security numbers of anyone covered.
            </h3>
            <p className="text-sm mt-1">
              This information is encrypted and NEVER sold.
            </p>
          </div>

          <div className="flex flex-col">
            {applicants.map((applicant, index) => (
              <div key={index}>
                <div className="flex flex-wrap items-center gap-4 py-3">
                  {applicant.applicant === 'primary' ? (
                    <MemberIcon className="w-12 h-12 shrink-0 text-primary" />
                  ) : (
                    <img
                      src={getApplicantIcon(applicant)}
                      alt={getApplicantRole(applicant)}
                      className="w-12 h-12 shrink-0"
                    />
                  )}
                  {/* Name & Role */}
                  <div className="flex flex-col min-w-0 shrink-0">
                    <span className="text-base font-bold leading-tight">
                      {applicant.firstName || ''}:
                    </span>
                    <span className="text-sm text-gray-500 leading-tight">
                      {getApplicantRole(applicant)}
                    </span>
                  </div>
                  {/* SSN Input */}
                  <div className="sm:ml-auto w-full sm:w-auto">
                    <FloatingInput
                      id={`ssn-${index}`}
                      label="SSN"
                      type="text"
                      value={ssnValues[index] || ''}
                      onChange={(e) => handleSsnChange(index, e.target.value)}
                      maxLength={11}
                      placeholder="###-##-####"
                      inputClass={cn(
                        'h-12 w-full sm:w-44 min-h-auto outline-none border border-[#B7B7B7] rounded-lg px-3 text-sm tracking-widest',
                        ssnErrors[index] && '!border-red-500 !border-2'
                      )}
                    />
                  </div>
                </div>
                {ssnErrors[index] && (
                  <p className="text-xs text-red-500 -mt-1 text-right sm:text-right">
                    {ssnErrors[index]}
                  </p>
                )}
                {index < applicants.length - 1 && (
                  <hr className="border-gray-200" />
                )}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};
